import type { TableChangesStorage } from '@/shared/components/data-grid/helpers'
import type {
	SRT_Cell,
	SRT_ColumnHelper,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '@/shared/components/master-table'
import type {
	CategoryModel,
	FieldSchemaModel,
	SalesEntityModel,
} from '@/shared/generated'
import type { TreeNodeType } from '@/shared/lib/tree'
import type { TFunction } from 'i18next'
import type { DataOption } from '../model/types'
import type { FieldCellProps } from '../ui/fields'

import React from 'react'
import { uniqBy } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'

import { getEntityFieldValue } from '@/entities/assortment/@x/column'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { parseFromValuesOrFunc } from '@/shared/components/master-table/utils/utils'
import { FieldType } from '@/shared/generated'
import { getTree } from '@/shared/lib/tree'

import BasicEditableField from '../ui/BasicEditableField'
import { BasicTextEditorField } from '../ui/BasicTextEditorField'
import { BasicTextField } from '../ui/BasicTextField'
import { fields } from '../ui/fields'

/**
 * Create fuction middleware to assign renderer to column config based on schema name and mapping of renderer components
 * @param config - config for assignRenderers function
 * @returns - function that accepts ColumnOptionConfig and returns ColumnOptionConfig
 */
export function createColumnProcessor<T extends SRT_RowData>(
	{
		columnHelper,
		getCell,
		getAccessorFn,
		enableCellEditing,
		tableChangesStorage,
	}: {
		tableChangesStorage?: TableChangesStorage
		columnHelper: SRT_ColumnHelper<T>
		enableCellEditing?:
			| string
			| boolean
			| (({
					cell,
					table,
					row,
			  }: {
					cell: SRT_Cell<T>
					table: SRT_TableInstance<T>
					row: SRT_Row<T>
			  }) => boolean | string)

		getCell?: (
			field: FieldSchemaModel
		) => React.FC<FieldCellProps<keyof typeof fields>> | null | undefined | any
		getAccessorFn: (field: FieldSchemaModel) => (row: T) => any
	},
	t: TFunction
) {
	const getCellComponent = (field: FieldSchemaModel) => {
		const customRenderer = getCell?.(field)
		if (customRenderer) {
			return {
				CellComponent: customRenderer,
				CellEditorComponent: customRenderer,
			}
		} else if (field.field_type && fields[field.field_type]) {
			return {
				CellComponent: fields[field.field_type].cellComponent,
				CellEditorComponent: fields[field.field_type].cellEditorComponent,
			}
		}

		return {
			CellComponent: BasicTextField,
			CellEditorComponent: BasicTextEditorField,
		}
	}

	return (
		field: FieldSchemaModel & {
			getChoices(): Record<string, DataOption | TreeNodeType>
		}
	) => {
		const id = field.id
		const header = t(field.name, { ns: 'fields', defaultValue: field.name })
		const description = field.description
			? t(field.description, { ns: 'fields', defaultValue: field.description })
			: undefined

		const choices = field.getChoices() || {}

		const isEditable = !!(field.params.editable && tableChangesStorage)

		const { CellComponent, CellEditorComponent } = getCellComponent(field)

		return columnHelper.accessor(getAccessorFn(field), {
			id,
			description,
			header,
			size: field.params.width || 150,
			// Only show description tooltip if header and description are different
			enableDescriptionTooltip: header !== description && !!description,
			enablePinning: !!field.params.lockable,
			enableSorting: !!field.params.sortable,
			meta: {
				choices,
			},
			Cell: ({ renderedCellValue, cellValue, cell, row, table }) => {
				const enableCellEditingResult = parseFromValuesOrFunc(
					enableCellEditing,
					{
						cell,
						table,
						row,
					}
				)
				const isEnableCellEditing =
					typeof enableCellEditingResult === 'boolean'
						? enableCellEditingResult
						: false
				const disabledReason =
					typeof enableCellEditingResult === 'string'
						? enableCellEditingResult
						: undefined

				const hasReference =
					field.reference &&
					// Skip sales_entity_ids and sales_entity_names, as they should use the cell value, not the reference from the schema
					field.id !== 'sales_entity_ids' &&
					field.id !== 'sales_entity_names'

				if (hasReference) {
					const props = {
						disabled: !isEnableCellEditing,
						disabledReason,
						value: getEntityFieldValue(row.original, field.reference!.accessor),
						choices,
						field,
						row: row.original,
						tableRow: row,
					}

					return (
						<ErrorBoundary
							fallback={
								<AlertTriangleIcon className='size-4 text-destructive' />
							}
						>
							{isEditable ? (
								<BasicEditableField {...props} control={tableChangesStorage}>
									<CellEditorComponent originalValue={renderedCellValue} />
								</BasicEditableField>
							) : (
								<CellComponent {...props} />
							)}
						</ErrorBoundary>
					)
				}

				if (
					Array.isArray(renderedCellValue) ||
					renderedCellValue === null ||
					renderedCellValue === undefined ||
					renderedCellValue === '' ||
					typeof renderedCellValue === 'string' ||
					typeof renderedCellValue === 'number' ||
					typeof renderedCellValue === 'boolean'
				) {
					const props = {
						disabled: !isEnableCellEditing,
						disabledReason,
						value: cellValue,
						row: row.original,
						tableRow: row,
						table,
						choices,
						field,
					}

					return (
						<ErrorBoundary
							fallback={
								<AlertTriangleIcon className='size-4 text-destructive' />
							}
						>
							{isEditable ? (
								<BasicEditableField {...props} control={tableChangesStorage}>
									<CellEditorComponent originalValue={renderedCellValue} />
								</BasicEditableField>
							) : (
								<CellComponent {...props} />
							)}
						</ErrorBoundary>
					)
				}

				return renderedCellValue
			},
		})
	}
}

export type FieldSchemaProcessor<T extends SRT_RowData> = ReturnType<
	typeof createColumnProcessor<T>
>

export const prepareList = (list: any[], field: FieldSchemaModel) => {
	if (!list) {
		return []
	}
	if (field.reference?.entity === 'category') {
		return uniqBy(
			'id',
			list?.map((cat: CategoryModel) => ({ ...cat, parent: cat.parent_id }))
		)
	}
	if (field.reference?.entity === 'sales_entity') {
		return list?.map((entity: SalesEntityModel) => ({
			parent: entity.parent_id,
			id: String(entity.id),
			name: entity[
				(field.reference?.field as keyof typeof entity) || 'name'
			] as string,
			level: entity.sales_level,
		}))
	}

	return list || []
}

export const createChoices = ({
	entities,
	schema,
	getChoiceExtraParams,
}: {
	entities: Record<string, any[]>
	schema: FieldSchemaModel[]
	getChoiceExtraParams?: <T>(field: FieldSchemaModel, entity: T) => any
}) => {
	const choices: Record<string, DataOption[] | TreeNodeType[]> = {}
	// Create choices for each field in schema
	for (const field of schema) {
		if (field.reference?.entity) {
			// FIXME: this is a temporary solution to get the tree items
			const list = prepareList(
				entities[field.reference.entity as keyof typeof entities],
				field
			)
			if (field.field_type === FieldType.Tree) {
				choices[field.id] = getTree(list)
			} else {
				choices[field.id] =
					list?.map((entity) => {
						const extraParams = getChoiceExtraParams?.(field, entity) || {}

						return {
							value: String(entity.id),
							label: entity[
								(field.reference?.field as keyof typeof entity) || 'name'
							] as string,
							...extraParams,
						}
					}) || []
			}
		} else if (field.config?.enum?.length) {
			choices[field.id] = field.config.enum
				.filter((value) => value != null)
				.map((value) => ({ value, label: value }))
		}
	}

	return choices
}
